import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  CustomFieldRecord,
  CustomFieldStatus,
  ListCustomFieldsRequest,
} from "@/shared/domain/customFields";
import { PaginatedResponse } from "@/shared/http";

import { customFieldQueryKeys } from "./customFieldQueryKeys";

const TRANSITIONAL_POLL_RATE_MS = 2000;

export interface UseCustomFieldsParams {
  request: ListCustomFieldsRequest;
  /** Override the resolved org; defaults to the current org. */
  orgId?: string;
}

/**
 * Lists custom-field definitions for the current org, polling while any field
 * is in a transitional state (Creating/Deleting) so lifecycle transitions
 * surface without a manual refresh. Polling stops once every field is stable.
 */
export function useCustomFields({
  request,
  orgId,
}: UseCustomFieldsParams): UseQueryResult<
  PaginatedResponse<CustomFieldRecord>,
  Error
> {
  const { customFieldService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: customFieldQueryKeys.list(resolvedOrgId, request),
    queryFn: ({ signal }) =>
      customFieldService.listCustomFields(request, {
        abortSignal: signal,
        orgId: resolvedOrgId,
      }),
    refetchInterval: (query) => {
      const hasTransitionalField = query.state.data?.items.some(
        (field) =>
          field.status === CustomFieldStatus.Creating ||
          field.status === CustomFieldStatus.Deleting,
      );
      return hasTransitionalField ? TRANSITIONAL_POLL_RATE_MS : false;
    },
  });
}
